import React, { useMemo } from 'react';
import {
  Alert,
  AlertDescription,
  Badge,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@roger/ui-components';
import {
  AlertCircle,
  Info,
  Calendar,
  User,
  Stethoscope,
  CheckCircle,
  Loader2,
  RefreshCw,
  AlertTriangle,
  Hospital,
  Upload,
  Archive,
  HeartPulse,
  X,
  Pill,
  CalendarClock,
  CheckCircle2,
} from 'lucide-react';
import {
  CodingVisit,
  rerunCodingDiagnosis,
  getRogerDocClinics,
  RogerDocClinic,
  EMRSource,
} from '../services/rogerDocApi';
import { useStore } from '../store';
import { ChecklistItem } from './intake/shared/ChecklistItem';
import type { ChecklistItemDetails } from '../types/codingData.types';
import {
  CodingSearchFilters,
  type CodingSearchParams,
  type ClinicBranchFilter,
} from './CodingSearchFilters';
import { CodingActionsMenu } from './CodingActionsMenu';
import { FeatureFlag } from '../constants/featureFlags';
import {
  getVisitTypeLabel,
  getMedicalCodingStatusLabel,
  getDocumentStatusBooleans,
  getBranchName,
} from '../utils/codingVisitUtils';
import { exportCodingVisitsToExcel } from '../utils/codingVisitExport';
import { formatDateForDisplay, getTodayInPT } from '../utils/dateUtils';

export type { CodingSearchParams, ClinicBranchFilter };

interface CodingSearchFormProps {
  searchParams: CodingSearchParams;
  onSearch: (params: CodingSearchParams) => void;
  onClearFilters: () => void;
  searching: boolean;
  searchResults: CodingVisit[];
  hasSearched: boolean;
  selectedVisit: CodingVisit | null;
  loading: boolean;
  onVisitSelected: (visit: CodingVisit) => void;
  onChangeSelection: () => void;
  onRerunSuccess?: (diagnosisId: string) => void;
  totalCount?: number | null;
  hasMore?: boolean;
  onLoadMore?: () => void;
  onLoadAll?: () => void;
  loadingMore?: boolean;
}

// Reusable hover + click popover component
const HoverClickPopover: React.FC<{
  trigger: React.ReactNode;
  content: React.ReactNode;
  side?: 'top' | 'bottom' | 'left' | 'right';
  align?: 'start' | 'center' | 'end';
  contentClassName?: string;
}> = ({
  trigger,
  content,
  side = 'top',
  align = 'center',
  contentClassName = 'max-w-xs',
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const handleMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setIsOpen(false), 150);
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <div
          className='cursor-pointer'
          onClick={e => e.stopPropagation()}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {trigger}
        </div>
      </PopoverTrigger>
      <PopoverContent
        side={side}
        className={`${contentClassName} z-50`}
        align={align}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {content}
      </PopoverContent>
    </Popover>
  );
};

// Post Coding Checks Indicator with hover + click support
const PostCodingChecksIndicator: React.FC<{
  activeChecks: Array<{ title: string; severity: string }>;
  severityColor: string;
}> = ({ activeChecks, severityColor }) => {
  return (
    <HoverClickPopover
      trigger={
        <div
          className={`flex items-center gap-1 ${severityColor} hover:opacity-80 transition-opacity`}
        >
          <AlertTriangle className='w-4 h-4' />
          <span className='text-xs font-medium'>{activeChecks.length}</span>
        </div>
      }
      content={
        <div className='space-y-1'>
          <p className='font-semibold text-xs'>Post Coding Checks</p>
          <ul className='text-xs space-y-0.5'>
            {activeChecks.map((check, idx) => (
              <li key={idx} className='flex items-start gap-1'>
                <span
                  className={`inline-block w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${
                    check.severity === 'HIGH'
                      ? 'bg-red-500'
                      : check.severity === 'MEDIUM'
                        ? 'bg-orange-500'
                        : check.severity === 'LOW'
                          ? 'bg-yellow-500'
                          : 'bg-blue-500'
                  }`}
                />
                {check.title}
              </li>
            ))}
          </ul>
        </div>
      }
    />
  );
};

const CodingSearchForm: React.FC<CodingSearchFormProps> = ({
  searchParams,
  onSearch,
  onClearFilters,
  searching,
  searchResults,
  hasSearched,
  selectedVisit,
  loading,
  onVisitSelected,
  onChangeSelection,
  onRerunSuccess,
  totalCount,
  hasMore,
  onLoadMore,
  onLoadAll,
  loadingMore,
}) => {
  const [rerunModalOpen, setRerunModalOpen] = React.useState(false);
  const [visitToRerun, setVisitToRerun] = React.useState<CodingVisit | null>(
    null
  );
  const [rerunLoading, setRerunLoading] = React.useState(false);
  const [rerunResult, setRerunResult] = React.useState<{
    success: boolean;
    message: string;
  } | null>(null);

  // Selected visits for bulk actions
  const [selectedVisitIds, setSelectedVisitIds] = React.useState<Set<string>>(
    new Set()
  );

  // Clinics state
  const [clinics, setClinics] = React.useState<RogerDocClinic[]>([]);
  const user = useStore(state => state.user);
  const hasFeatureFlag = useStore(state => state.hasFeatureFlag);
  const includeTherapyEvalTags = hasFeatureFlag(
    FeatureFlag.MEDICAL_CODING_CONSIDER_THERAPY_EVAL,
    'rogerdoc'
  );
  const clinicOptions = useMemo(
    () =>
      [...clinics]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(c => ({ value: c.name, label: c.name })),
    [clinics]
  );

  // Fetch clinics on mount
  React.useEffect(() => {
    const fetchClinics = async () => {
      if (!user?.agency?.system_name) return;
      try {
        const fetched: RogerDocClinic[] = await getRogerDocClinics(
          user.agency.system_name
        );
        setClinics(fetched);
      } catch (error) {
        console.error('Failed to fetch clinics:', error);
      }
    };
    fetchClinics();
  }, [user?.agency?.system_name]);

  const handleConfirmRerun = async () => {
    if (!visitToRerun) return;
    setRerunLoading(true);
    try {
      const result = await rerunCodingDiagnosis(
        visitToRerun.diagnosis_id,
        EMRSource.WELLSKY, // currently rerunning is only allowed for wellsky
        undefined,
        user?.email
      );
      if (result.success) {
        setRerunModalOpen(false);
        setVisitToRerun(null);
        setRerunResult(null);
        onRerunSuccess?.(visitToRerun.diagnosis_id);
      } else {
        setRerunResult({ success: false, message: result.message });
      }
    } catch {
      setRerunResult({
        success: false,
        message: 'An error occurred while communicating with the server.',
      });
    } finally {
      setRerunLoading(false);
    }
  };

  const handleCloseRerunModal = () => {
    setRerunModalOpen(false);
    setVisitToRerun(null);
    setRerunResult(null);
  };

  // Selection handlers for bulk actions
  const toggleVisitSelection = (diagnosisId: string) => {
    setSelectedVisitIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(diagnosisId)) {
        newSet.delete(diagnosisId);
      } else {
        newSet.add(diagnosisId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    const selectableVisits = searchResults.filter(v => v.status !== 'FAILED');
    if (selectedVisitIds.size === selectableVisits.length) {
      setSelectedVisitIds(new Set());
    } else {
      setSelectedVisitIds(new Set(selectableVisits.map(v => v.diagnosis_id)));
    }
  };

  const clearSelection = () => {
    setSelectedVisitIds(new Set());
  };

  // Helper to extract active post coding checks from a visit
  const getActivePostCodingChecks = (visit: CodingVisit) => {
    if (!visit.post_coding_checks) return [];

    const activeChecks: Array<{
      title: string;
      severity: string;
    }> = [];

    // post_coding_checks is an object with check names as keys
    for (const [, check] of Object.entries(visit.post_coding_checks)) {
      if (check.active) {
        activeChecks.push({
          title: check.title,
          severity: check.severity,
        });
      }
    }

    return activeChecks;
  };

  // Get the highest severity from a list of checks
  const getHighestSeverity = (
    checks: Array<{ severity: string }>
  ): string | null => {
    const severityOrder = ['HIGH', 'MEDIUM', 'LOW', 'INFO'];
    for (const severity of severityOrder) {
      if (checks.some(c => c.severity === severity)) {
        return severity;
      }
    }
    return null;
  };

  // Get icon color based on severity
  const getSeverityColor = (severity: string | null) => {
    switch (severity) {
      case 'HIGH':
        return 'text-red-500';
      case 'MEDIUM':
        return 'text-orange-500';
      case 'LOW':
        return 'text-yellow-500';
      case 'INFO':
        return 'text-blue-500';
      default:
        return 'text-gray-500';
    }
  };

  // Helper to compute document validation statuses with details (matching Intake3 pattern)
  type DocStatus = 'complete' | 'missing' | 'not-applicable';

  const getDocumentStatusesWithDetails = (
    visit: CodingVisit
  ): {
    statuses: {
      orderAttached: DocStatus;
      orderSigned: DocStatus;
      f2fAttached: DocStatus;
      f2fSigned: DocStatus;
    };
    details: Record<string, ChecklistItemDetails>;
  } => {
    const doc = getDocumentStatusBooleans(visit);
    const medicalData = visit.medical_data;
    const homeHealthOrders = medicalData?.home_health_orders ?? [];
    const faceToFaceVisits = medicalData?.face_to_face_visits ?? [];

    const details: Record<string, ChecklistItemDetails> = {
      orderAttached: { items: homeHealthOrders },
      orderSigned: { items: homeHealthOrders, showOnlySignedItems: true },
      f2fAttached: { items: faceToFaceVisits },
      f2fSigned: { items: faceToFaceVisits, showOnlySignedItems: true },
    };

    return {
      statuses: {
        orderAttached: doc.orderAttached ? 'complete' : 'missing',
        orderSigned: doc.orderSigned ? 'complete' : 'missing',
        f2fAttached: doc.f2fAttached ? 'complete' : 'missing',
        f2fSigned: doc.f2fSigned ? 'complete' : 'missing',
      },
      details,
    };
  };

  const handleExportToExcel = () => {
    const selectedVisits = searchResults.filter(v =>
      selectedVisitIds.has(v.diagnosis_id)
    );
    if (selectedVisits.length === 0) return;
    const dateStr = getTodayInPT();
    void exportCodingVisitsToExcel(
      selectedVisits,
      `RogerCodingDashboardExport_${dateStr}`,
      { includeTherapyEvalTags: includeTherapyEvalTags, clinics }
    );
  };

  return (
    <>
      {!selectedVisit && (
        <CodingSearchFilters
          defaultValues={searchParams}
          onSearch={onSearch}
          onClear={onClearFilters}
          searching={searching}
          loading={loading}
          clinicOptions={clinicOptions}
          rawClinics={clinics}
        />
      )}

      <div className='mb-6'>
        {/* Collapsed View - Selected Visit */}
        {selectedVisit && (
          <>
            <Button
              variant='outline'
              onClick={onChangeSelection}
              disabled={loading}
              className='mb-4 border-blue-300 text-blue-700 hover:bg-blue-50'
            >
              ← Back to Visit List
            </Button>

            <Card className='bg-blue-50 border-blue-300'>
              <CardContent className='p-4'>
                <div className='flex items-start justify-between'>
                  <div className='flex-1'>
                    <div className='flex items-center gap-2 mb-2'>
                      <span className='font-semibold text-gray-900 text-base'>
                        {selectedVisit.visit.patient_first_name}{' '}
                        {selectedVisit.visit.patient_last_name}
                      </span>
                      {selectedVisit.visit.patient_id && (
                        <Badge
                          variant='secondary'
                          className='text-xs font-mono'
                        >
                          MRN: {selectedVisit.visit.patient_id}
                        </Badge>
                      )}
                      <Badge
                        className={`text-xs whitespace-pre-line text-center ${
                          selectedVisit.status === 'COMPLETED'
                            ? 'bg-green-100 text-green-800'
                            : selectedVisit.status === 'FAILED' ||
                                selectedVisit.status === 'CANCELLED'
                              ? 'bg-orange-100 text-orange-800'
                              : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {getMedicalCodingStatusLabel(selectedVisit.status)}
                      </Badge>
                    </div>

                    <div className='flex items-center gap-6 text-sm text-gray-600'>
                      <div className='flex items-center gap-1'>
                        <Calendar className='w-4 h-4' />
                        <span>
                          {formatDateForDisplay(
                            selectedVisit.visit.visit_date_date
                          )}
                        </span>
                      </div>

                      <div className='flex items-center gap-1'>
                        <User className='w-4 h-4' />
                        <span>{selectedVisit.visit.nurse_username}</span>
                      </div>

                      <div className='flex items-center gap-1'>
                        <Stethoscope className='w-4 h-4' />
                        <span>
                          {getVisitTypeLabel(selectedVisit.visit.visit_type)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className='flex items-center gap-2'>
                    {loading && (
                      <Loader2 className='h-4 w-4 animate-spin text-blue-600' />
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* Search Results */}
        {!selectedVisit && searching && (
          <div className='flex justify-center items-center py-12'>
            <div className='flex items-center gap-3 text-gray-500'>
              <Loader2 className='h-5 w-5 animate-spin text-blue-600' />
              <span className='text-sm'>Loading visits...</span>
            </div>
          </div>
        )}

        {/* Expanded Search View */}
        {!selectedVisit && (
          <>
            {searchResults.length > 0 && (
              <>
                <div className='flex justify-end mb-2'>
                  <span className='text-xs text-gray-500'>
                    {totalCount != null
                      ? `Showing ${searchResults.length} of ${totalCount} result${totalCount !== 1 ? 's' : ''}`
                      : `${searchResults.length} result${searchResults.length !== 1 ? 's' : ''}`}
                  </span>
                </div>

                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className='w-10'>
                        <Checkbox
                          checked={
                            searchResults.filter(v => v.status !== 'FAILED')
                              .length > 0 &&
                            selectedVisitIds.size ===
                              searchResults.filter(v => v.status !== 'FAILED')
                                .length
                          }
                          onCheckedChange={toggleSelectAll}
                          aria-label='Select all'
                        />
                      </TableHead>
                      <TableHead>Patient</TableHead>
                      <TableHead>Visit Info</TableHead>
                      <TableHead>Location & Nurse</TableHead>
                      <TableHead>Documents</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {searchResults
                      .sort((a, b) => {
                        // Sort non-disabled visits first, then disabled visits (CANCELLED or FAILED)
                        const aDisabled = a.status === 'FAILED';
                        const bDisabled = b.status === 'FAILED';

                        if (aDisabled && !bDisabled) return 1;
                        if (!aDisabled && bDisabled) return -1;

                        // Within each group, maintain original order
                        return 0;
                      })
                      .map(visit => {
                        const isFailed = visit.status === 'FAILED';
                        const activeChecks = getActivePostCodingChecks(visit);
                        const highestSeverity =
                          getHighestSeverity(activeChecks);
                        const severityColor = getSeverityColor(highestSeverity);
                        const { statuses, details } =
                          getDocumentStatusesWithDetails(visit);

                        const isSelected = selectedVisitIds.has(
                          visit.diagnosis_id
                        );

                        return (
                          <TableRow
                            key={visit.diagnosis_id}
                            className={`cursor-pointer hover:bg-blue-50 ${
                              isFailed
                                ? 'bg-red-50/30'
                                : isSelected
                                  ? 'bg-blue-50'
                                  : ''
                            }`}
                            onClick={e => {
                              if (isFailed) {
                                setVisitToRerun(visit);
                                setRerunResult(null);
                                setRerunModalOpen(true);
                              } else if (e.metaKey || e.ctrlKey) {
                                // Command/Ctrl + click opens in new tab
                                window.open(
                                  `/coding/${visit.diagnosis_id}`,
                                  '_blank'
                                );
                              } else {
                                onVisitSelected(visit);
                              }
                            }}
                          >
                            {/* Checkbox */}
                            <TableCell onClick={e => e.stopPropagation()}>
                              <Checkbox
                                checked={isSelected}
                                onCheckedChange={() =>
                                  toggleVisitSelection(visit.diagnosis_id)
                                }
                                disabled={isFailed}
                                aria-label={`Select ${visit.visit.patient_first_name} ${visit.visit.patient_last_name}`}
                              />
                            </TableCell>

                            {/* Patient Name & MRN */}
                            <TableCell>
                              <div className='flex flex-col'>
                                <span className='font-semibold text-gray-900'>
                                  {visit.visit.patient_first_name}{' '}
                                  {visit.visit.patient_last_name}
                                </span>
                                {visit.visit.patient_id && (
                                  <Badge
                                    variant='secondary'
                                    className='text-xs font-mono w-fit mt-1'
                                  >
                                    {visit.visit.patient_id}
                                  </Badge>
                                )}
                              </div>
                            </TableCell>

                            {/* Visit Date & Type */}
                            <TableCell>
                              <div className='flex flex-col gap-1 text-sm text-gray-600'>
                                <div className='flex items-center gap-1'>
                                  <Calendar className='w-4 h-4' />
                                  <span>
                                    {formatDateForDisplay(
                                      visit.visit.visit_date_date
                                    )}
                                  </span>
                                </div>
                                <div className='flex items-center gap-1'>
                                  <Stethoscope className='w-4 h-4 text-gray-500' />
                                  <span>
                                    {getVisitTypeLabel(visit.visit.visit_type)}
                                  </span>
                                </div>
                              </div>
                            </TableCell>

                            {/* Location & Nurse */}
                            <TableCell>
                              <div className='flex flex-col gap-1 text-sm text-gray-600'>
                                {visit.visit.location &&
                                  visit.visit.location !== 'null' && (
                                    <div className='flex items-center gap-1'>
                                      <Hospital className='w-4 h-4 text-gray-500' />
                                      <span>{visit.visit.location}</span>
                                    </div>
                                  )}
                                {visit.visit.branch_id &&
                                  visit.visit.branch_id !== 'null' && (
                                    <div className='flex items-center gap-1'>
                                      <span className='ml-5 text-xs text-gray-500'>
                                        {getBranchName(
                                          visit.visit.branch_id,
                                          clinics
                                        )}
                                      </span>
                                    </div>
                                  )}
                                {visit.visit.nurse_last_name && (
                                  <div className='flex items-center gap-1'>
                                    <User className='w-4 h-4 text-gray-500' />
                                    <span>
                                      {visit.visit.nurse_first_name}{' '}
                                      {visit.visit.nurse_last_name}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </TableCell>

                            {/* Document Validation */}
                            <TableCell onClick={e => e.stopPropagation()}>
                              {visit.tags?.no_documents ? (
                                <div className='text-sm text-amber-600 font-medium'>
                                  No PDFs attached under episode
                                </div>
                              ) : (
                                visit.medical_data && (
                                  <div className='grid grid-cols-2 gap-x-2'>
                                    <ChecklistItem
                                      label='Order Attached'
                                      status={statuses.orderAttached}
                                      details={details.orderAttached}
                                      variant='compact'
                                    />
                                    <ChecklistItem
                                      label='Order Signed'
                                      status={statuses.orderSigned}
                                      details={details.orderSigned}
                                      variant='compact'
                                    />
                                    <ChecklistItem
                                      label='F2F Attached'
                                      status={statuses.f2fAttached}
                                      details={details.f2fAttached}
                                      variant='compact'
                                    />
                                    <ChecklistItem
                                      label='F2F Signed'
                                      status={statuses.f2fSigned}
                                      details={details.f2fSigned}
                                      variant='compact'
                                    />
                                  </div>
                                )
                              )}
                            </TableCell>

                            {/* Status */}
                            <TableCell>
                              <div className='flex flex-col gap-1'>
                                {/* Row 1: Status Badge */}
                                <Badge
                                  className={`px-2 py-1 text-xs w-fit whitespace-pre-line ${
                                    visit.status === 'COMPLETED'
                                      ? 'bg-green-100 text-green-800 hover:bg-green-100'
                                      : visit.status === 'FAILED' ||
                                          visit.status === 'CANCELLED'
                                        ? 'bg-orange-100 text-orange-800 hover:bg-orange-100'
                                        : 'bg-gray-100 text-gray-800 hover:bg-gray-100'
                                  }`}
                                >
                                  {getMedicalCodingStatusLabel(visit.status)}
                                </Badge>
                                {/* Row 2: Tag icons | Diagnosis code | Post Coding Checks */}
                                <div className='flex items-center gap-2'>
                                  {/* Tag indicators - undefined treated as false */}
                                  <HoverClickPopover
                                    trigger={
                                      <Upload
                                        className={`w-4 h-4 ${visit.tags?.EMR_filled ? 'text-green-500' : 'text-gray-400'}`}
                                      />
                                    }
                                    content={
                                      <p className='text-xs font-medium'>
                                        Filled into EMR:{' '}
                                        {visit.tags?.EMR_filled ? 'Yes' : 'No'}
                                      </p>
                                    }
                                    contentClassName='w-fit'
                                  />
                                  <span className='text-gray-300'>|</span>
                                  <HoverClickPopover
                                    trigger={
                                      <Archive
                                        className={`w-4 h-4 ${visit.tags?.archived ? 'text-orange-500' : 'text-gray-400'}`}
                                      />
                                    }
                                    content={
                                      <p className='text-xs font-medium'>
                                        Archived:{' '}
                                        {visit.tags?.archived ? 'Yes' : 'No'}
                                      </p>
                                    }
                                    contentClassName='w-fit'
                                  />
                                  <span className='text-gray-300'>|</span>
                                  <HoverClickPopover
                                    trigger={
                                      <HeartPulse
                                        className={`w-4 h-4 ${visit.tags?.has_multiple_chronic_conditions ? 'text-purple-500' : 'text-gray-400'}`}
                                      />
                                    }
                                    content={
                                      <p className='text-xs font-medium'>
                                        Multiple Chronic Conditions:{' '}
                                        {visit.tags
                                          ?.has_multiple_chronic_conditions
                                          ? 'Yes'
                                          : 'No'}
                                      </p>
                                    }
                                    contentClassName='w-fit'
                                  />
                                  <span className='text-gray-300'>|</span>
                                  <HoverClickPopover
                                    trigger={
                                      <Pill
                                        className={`w-4 h-4 ${visit.tags?.medication_profile_complete ? 'text-green-500' : 'text-gray-400'}`}
                                      />
                                    }
                                    content={
                                      <p className='text-xs font-medium'>
                                        Medication Profile Complete:{' '}
                                        {visit.tags?.medication_profile_complete
                                          ? 'Yes'
                                          : 'No'}
                                      </p>
                                    }
                                    contentClassName='w-fit'
                                  />
                                  {includeTherapyEvalTags && (
                                    <>
                                      <span className='text-gray-300'>|</span>
                                      <HoverClickPopover
                                        trigger={
                                          <CalendarClock
                                            className={`w-4 h-4 ${visit.tags?.eval_scheduled ? 'text-slate-600' : 'text-gray-400'}`}
                                          />
                                        }
                                        content={
                                          <p className='text-xs font-medium'>
                                            Eval Scheduled:{' '}
                                            {visit.tags?.eval_scheduled
                                              ? 'Yes'
                                              : 'No'}
                                          </p>
                                        }
                                        contentClassName='w-fit'
                                      />
                                      <span className='text-gray-300'>|</span>
                                      <HoverClickPopover
                                        trigger={
                                          <CheckCircle2
                                            className={`w-4 h-4 ${visit.tags?.eval_completed ? 'text-emerald-600' : 'text-gray-400'}`}
                                          />
                                        }
                                        content={
                                          <p className='text-xs font-medium'>
                                            Eval Completed:{' '}
                                            {visit.tags?.eval_completed
                                              ? 'Yes'
                                              : 'No'}
                                          </p>
                                        }
                                        contentClassName='w-fit'
                                      />
                                    </>
                                  )}
                                  {visit.primary_diagnosis?.diagnosis?.name && (
                                    <span className='text-gray-300'>|</span>
                                  )}
                                  {visit.primary_diagnosis?.diagnosis?.name && (
                                    <>
                                      {visit.primary_diagnosis?.diagnosis
                                        ?.desc ? (
                                        <HoverClickPopover
                                          trigger={
                                            <span className='text-sm text-gray-500 hover:opacity-80 transition-opacity'>
                                              {
                                                visit.primary_diagnosis
                                                  ?.diagnosis?.name
                                              }
                                            </span>
                                          }
                                          content={
                                            <p className='text-sm'>
                                              {
                                                visit.primary_diagnosis
                                                  ?.diagnosis?.desc
                                              }
                                            </p>
                                          }
                                        />
                                      ) : (
                                        <span className='text-sm text-gray-500'>
                                          {
                                            visit.primary_diagnosis?.diagnosis
                                              ?.name
                                          }
                                        </span>
                                      )}
                                    </>
                                  )}
                                  {activeChecks.length > 0 && (
                                    <>
                                      <span className='text-gray-300'>|</span>
                                      <PostCodingChecksIndicator
                                        activeChecks={activeChecks}
                                        severityColor={severityColor}
                                      />
                                    </>
                                  )}
                                </div>
                              </div>
                            </TableCell>

                            {/* Action indicator */}
                            <TableCell>
                              <span
                                className={`text-xs font-semibold flex items-center gap-1 ${
                                  isFailed ? 'text-red-600' : 'text-blue-600'
                                }`}
                              >
                                {isFailed ? 'Rerun' : 'Select'}
                                {isFailed ? (
                                  <RefreshCw className='w-3 h-3' />
                                ) : (
                                  <CheckCircle className='w-3 h-3' />
                                )}
                              </span>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
                {hasMore && (
                  <div className='flex justify-center mt-4 gap-4'>
                    <Button
                      variant='outline'
                      onClick={onLoadMore}
                      disabled={loadingMore}
                    >
                      {loadingMore ? (
                        <>
                          <Loader2 className='w-4 h-4 mr-2 animate-spin' />
                          Loading...
                        </>
                      ) : (
                        'Load More'
                      )}
                    </Button>
                    <Button
                      variant='outline'
                      onClick={onLoadAll}
                      disabled={loadingMore}
                    >
                      {loadingMore ? (
                        <>
                          <Loader2 className='w-4 h-4 mr-2 animate-spin' />
                          Loading All...
                        </>
                      ) : (
                        'Load All'
                      )}
                    </Button>
                  </div>
                )}
              </>
            )}

            {searchResults.length === 0 && hasSearched && !searching && (
              <Alert className='border-blue-200 bg-blue-50'>
                <AlertDescription className='text-blue-800 flex items-center gap-2'>
                  <Info className='h-4 w-4 text-blue-600' />
                  No visits are available for selection with the current
                  filters. Please try adjusting your search filters or expanding
                  your date range.
                </AlertDescription>
              </Alert>
            )}
          </>
        )}
      </div>

      {/* Floating bulk actions menu */}
      {selectedVisitIds.size > 0 && (
        <div className='fixed bottom-6 left-1/2 -translate-x-1/2 z-50'>
          <div className='flex items-center gap-2 bg-primary text-primary-foreground px-2 py-2 rounded-lg shadow-lg'>
            <div className='flex items-center gap-2 h-7 px-2 bg-black/20 rounded border border-white/10'>
              <span className='text-sm'>{selectedVisitIds.size} selected</span>
              <button
                onClick={clearSelection}
                className='hover:bg-black/20 rounded p-0.5'
                aria-label='Clear selection'
              >
                <X className='w-3.5 h-3.5' />
              </button>
            </div>
            <CodingActionsMenu
              diagnosisIds={Array.from(selectedVisitIds)}
              onActionComplete={() => {
                clearSelection();
                onSearch(searchParams);
              }}
              onExportToExcel={handleExportToExcel}
            />
          </div>
        </div>
      )}

      <Dialog open={rerunModalOpen} onOpenChange={handleCloseRerunModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rerun Medical Coding</DialogTitle>
          </DialogHeader>

          <div className='py-4'>
            {visitToRerun && !rerunResult && (
              <div className='space-y-4'>
                <p className='text-gray-600'>
                  Are you sure you want to rerun medical coding for the visit of{' '}
                  <strong>
                    {visitToRerun.visit.patient_first_name}{' '}
                    {visitToRerun.visit.patient_last_name}
                  </strong>{' '}
                  on{' '}
                  <strong>
                    {formatDateForDisplay(visitToRerun.visit.visit_date_date)}
                  </strong>
                  ?
                </p>
                <div className='bg-blue-50 p-3 rounded-md border border-blue-100 text-sm text-blue-800 flex gap-2'>
                  <Info className='w-4 h-4 mt-0.5 flex-shrink-0' />
                  <span>
                    This will start a new coding workflow for this visit.
                  </span>
                </div>
              </div>
            )}

            {rerunResult && !rerunResult.success && (
              <Alert className='border-red-200 bg-red-50'>
                <AlertCircle className='h-4 w-4 text-red-600' />
                <AlertDescription className='text-red-800'>
                  {rerunResult.message}
                </AlertDescription>
              </Alert>
            )}
          </div>

          <DialogFooter>
            {!rerunResult && (
              <>
                <Button
                  variant='outline'
                  onClick={handleCloseRerunModal}
                  disabled={rerunLoading}
                >
                  Cancel
                </Button>
                <Button onClick={handleConfirmRerun} disabled={rerunLoading}>
                  {rerunLoading && (
                    <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                  )}
                  Rerun Coding
                </Button>
              </>
            )}
            {rerunResult && !rerunResult.success && (
              <Button onClick={handleCloseRerunModal}>Close</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CodingSearchForm;
